import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase";
import trimSentence from "../utils/trimSentence";

const GENRE_KR = {
  "Fiction": "소설",
  "Literary Fiction": "소설",
  "Historical Fiction": "역사소설",
  "Science Fiction": "SF",
  "Fantasy": "판타지",
  "Romance": "로맨스",
  "Mystery": "미스터리",
  "Thriller": "스릴러",
  "Horror": "공포",
  "Biography & Autobiography": "전기",
  "Biography": "전기",
  "Autobiography": "자서전",
  "History": "역사",
  "Philosophy": "철학",
  "Psychology": "심리학",
  "Self-Help": "자기계발",
  "Business & Economics": "경영",
  "Business": "경영",
  "Economics": "경제",
  "Science": "과학",
  "Technology": "기술",
  "Computers": "IT",
  "Mathematics": "수학",
  "Poetry": "시",
  "Drama": "희곡",
  "Comics & Graphic Novels": "만화",
  "Art": "예술",
  "Music": "음악",
  "Cooking": "요리",
  "Travel": "여행",
  "Religion": "종교",
  "Social Science": "사회과학",
  "Political Science": "정치",
  "Education": "교육",
  "Health & Fitness": "건강",
  "Nature": "자연",
  "Sports & Recreation": "스포츠",
  "Juvenile Fiction": "아동문학",
  "Young Adult Fiction": "청소년소설",
  "Children": "아동",
  "Literary Collections": "소설",
  "Literary Criticism": "문학비평",
  "Language Arts & Disciplines": "언어",
  "Medical": "의학",
  "Law": "법학",
  "Architecture": "건축",
  "Design": "디자인",
  "Photography": "사진",
  "Body, Mind & Spirit": "인문학",
  "Family & Relationships": "가족",
  "Humor": "유머",
  "True Crime": "범죄실화",
  "Reference": "참고서",
  "Study Aids": "학습",
};

function toKoreanGenre(eng) {
  if (!eng) return "기타";
  const key = eng.split("/")[0].trim();
  if (GENRE_KR[key]) return GENRE_KR[key];
  const found = Object.keys(GENRE_KR).find(k => key.toLowerCase().includes(k.toLowerCase()) || k.toLowerCase().includes(key.toLowerCase()));
  return found ? GENRE_KR[found] : key;
}

// Kakao category → Korean genre
function kakaoGenre(category) {
  if (!category) return "기타";
  // Kakao returns categories like "소설/시/희곡>한국소설" or "경제경영>경영일반"
  const main = category.split(">")[0].trim();
  const map = {
    "소설/시/희곡": "소설", "소설": "소설", "시": "시", "희곡": "희곡",
    "경제경영": "경영", "자기계발": "자기계발", "인문학": "인문학",
    "역사": "역사", "종교": "종교", "사회과학": "사회과학",
    "과학": "과학", "예술/대중문화": "예술", "컴퓨터/모바일": "IT",
    "에세이": "에세이", "여행": "여행", "요리/살림": "요리",
    "건강/취미": "건강", "어린이": "아동", "유아": "아동",
    "만화": "만화", "잡지": "잡지", "외국어": "언어",
  };
  return map[main] || main;
}

async function searchKakao(query, target) {
  // Firebase Cloud Function -> Supabase Edge Function.
  // invoke 가 인증 헤더를 알아서 붙여준다.
  const { data, error } = await supabase.functions.invoke("search-books", {
    body: target ? { q: query, target } : { q: query },
  });
  if (error) return null;
  if (!data?.documents || data.documents.length === 0) return null;
  return data.documents.map(doc => ({
    googleId: doc.isbn || doc.url,
    // 10자리·13자리가 한 칸 띄워 함께 온다. 다듬는 것은 저장하는 쪽
    // (api/books.js 의 isbn13)이 하고, 여기서는 원문 그대로 실어 보낸다.
    isbn: doc.isbn || "",
    title: doc.title || "제목 없음",
    author: (doc.authors || ["저자 미상"]).join(", "),
    cover: doc.thumbnail || null,
    genre: kakaoGenre(doc.category) || "기타",
    // contents 는 250자에서 기계적으로 끊긴다. 문장 단위로 다듬어 넣는다.
    description: trimSentence(doc.contents),
    publishedDate: doc.datetime ? doc.datetime.slice(0, 4) : "",
  }));
}

/* 사람 이름처럼 생겼나 — 붙여 쓴 한글 두~네 글자.
   카카오 기본 검색은 제목을 먼저 보기 때문에, 시인 이름으로 찾으면
   '이현호의 포켓잉글리쉬' 같은 제목만 나오고 정작 그 사람의 책은 뒤로
   밀린다(실제로 30건 안에 한 권도 없었다). 이름처럼 보일 때만 저자
   검색(target=person)을 한 번 더 불러 합친다. */
const looksLikeName = (q) => /^[가-힣]{2,4}$/.test(String(q ?? "").trim());

/* 외래어 제목의 한 글자 차이. '제네레이션' 으로 찾으면 『제너레이션』(진
   트웬지)이 한 건도 안 나온다 — 카카오도 구글도 이 차이를 넘지 못한다.
   ㅔ 와 ㅓ 는 외래어를 옮길 때 가장 자주 갈리는 자리라, 한 음절만 바꿔
   본다. 두 글자 이상 바꾸면 엉뚱한 책이 나온다. */
const SWAP = { "네": "너", "너": "네", "레": "러", "러": "레", "메": "머", "머": "메",
  "데": "더", "더": "데", "테": "터", "터": "테", "베": "버", "버": "베", "페": "퍼", "퍼": "페" };
function spellVariants(q) {
  const out = [];
  for (let i = 0; i < q.length && out.length < 4; i++) {
    const alt = SWAP[q[i]];
    if (alt) out.push(q.slice(0, i) + alt + q.slice(i + 1));
  }
  return out;
}

/** 검색어와 확실히 맞는 것이 있나 — 제목이 같거나 그 말로 시작하거나, 저자가 같거나 */
const hasStrongHit = (list, q) =>
  (list || []).some((b) => titleScore(b.title, q) >= 2 || authorScore(b.author, q) >= 2.5);

/** 기본 검색 + (이름이면) 저자 검색을 합친다. 같은 책은 한 번만 남긴다. */
async function searchKakaoMerged(query) {
  const [general, byPerson] = await Promise.all([
    searchKakao(query),
    looksLikeName(query) ? searchKakao(query, "person") : Promise.resolve(null),
  ]);
  if (!general && !byPerson) return null;
  const out = [];
  const at = new Map();   // 같은 책이 두 번 들어오지 않게. 자리도 기억해 둔다
  const keyOf = (b) => `${b.isbn || ""}|${b.title}|${b.author}`;
  /* 저자 검색 결과를 앞에 둔다 — 이름으로 찾는 사람은 그 사람의 책을
     찾는 것이지, 이름이 제목에 든 문제집을 찾는 것이 아니다. */
  for (const b of [...(byPerson || []), ...(general || [])]) {
    const key = keyOf(b);
    if (at.has(key)) continue;
    at.set(key, out.length);
    out.push(b);
  }
  /* 찾는 책이 확실히 없어 보일 때만 한 글자 바꿔 다시 찾는다. 뒤에 붙이므로
     원래 검색어로 나온 책이 밀리지 않는다. */
  if (!hasStrongHit(out, query)) {
    for (const alt of spellVariants(query)) {
      const more = await searchKakao(alt);
      if (!more) continue;
      const strong = more.filter((b) => titleScore(b.title, alt) >= 2);
      if (!strong.length) continue;
      for (const b of strong) {
        /* 원래 검색어로는 한 글자도 안 맞으니 점수가 0 이 되어 목록 끝으로
           밀린다(『제너레이션』이 13번째였다). 바꾼 철자로 잰 점수를 실어
           보내되 반 칸 낮춘다 — 제대로 맞은 책보다는 뒤여야 한다. */
        const altScore = titleScore(b.title, alt) - 0.5;
        const key = keyOf(b);
        const i = at.get(key);
        /* 카카오가 원래 검색어에도 이 책을 (뒤쪽에) 끼워 준 경우가 있다.
           건너뛰면 점수를 못 받아 그대로 묻힌다 — 점수만 올린다. */
        if (i !== undefined) { out[i] = { ...out[i], altScore }; continue; }
        at.set(key, out.length);
        out.push({ ...b, altScore });
      }
      break;   // 한 번 찾으면 더 흔들지 않는다
    }
  }
  return out;
}

async function searchGoogle(query) {
  const res = await fetch(
    `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(query)}&maxResults=8`
  );
  if (!res.ok) return null;
  const data = await res.json();
  if (!data.items || data.items.length === 0) return null;
  return data.items.map(item => {
    const info = item.volumeInfo;
    return {
      googleId: item.id,
      // 카카오가 못 찾은 책은 여기로 넘어온다. 구글도 13자리를 준다.
      isbn: (info.industryIdentifiers || []).find(x => x.type === "ISBN_13")?.identifier || "",
      title: info.title || "제목 없음",
      author: (info.authors || ["저자 미상"]).join(", "),
      cover: info.imageLinks?.thumbnail?.replace("http://", "https://") || null,
      genre: toKoreanGenre(info.categories?.[0]),
      description: trimSentence(info.description),
      publishedDate: info.publishedDate || "",
    };
  });
}

async function searchOpenLibrary(query) {
  const res = await fetch(
    `https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=8&fields=key,title,author_name,cover_i,subject,first_publish_year`
  );
  if (!res.ok) return null;
  const data = await res.json();
  if (!data.docs || data.docs.length === 0) return null;
  return data.docs.map(doc => ({
    googleId: doc.key,
    title: doc.title || "제목 없음",
    author: (doc.author_name || ["저자 미상"]).join(", "),
    cover: doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-M.jpg` : null,
    genre: toKoreanGenre(doc.subject?.[0]),
    publishedDate: doc.first_publish_year ? String(doc.first_publish_year) : "",
  }));
}

/* 뒤에 붙은 권 번호를 떼어낸다. 『잠 1』『잠 2』 는 검색어 '잠' 과 같은
   책으로 봐야 한다. */
const stripVolume = (t) => String(t ?? "").replace(/\s*\d+\s*$/, "").trim();

/**
 * 검색어와 제목이 얼마나 맞는지.
 *
 * 카카오는 관련도 순으로 주는데, 검색어가 짧으면 정작 그 제목의 책이
 * 뒤로 밀린다 — '잠' 으로 찾으면 『잠』(베르나르 베르베르)이 13위였고
 * 10건만 받던 때에는 아예 없었다. 제목이 검색어와 같은 책을 앞으로
 * 끌어온다. 같은 점수 안에서는 카카오가 준 순서를 지킨다.
 */
function titleScore(title, q) {
  const t = String(title ?? "").trim();
  const bare = stripVolume(t);
  if (t === q || bare === q) return 3;
  if (t.startsWith(q)) return 2;
  if (t.includes(q)) return 1;
  return 0;
}

/** 보여줄 개수. 30건을 다 늘어놓으면 고르는 일이 되어버린다. */
const SHOW = 12;

/**
 * 검색어와 저자가 얼마나 맞는지.
 *
 * 제목만 보고 줄을 세웠더니 사람 이름으로 찾을 때 그 사람의 책이 없었다 —
 * '이현호' 는 『이현호의 포켓잉글리쉬』 열두 권이, '김소연' 은 수능 교재가
 * 자리를 다 차지했다. 저자가 검색어와 같으면 제목이 같은 것 다음으로 둔다.
 */
function authorScore(author, q) {
  const names = String(author ?? "").split(/[,;]/).map((a) => a.trim()).filter(Boolean);
  /* 그 사람이 혼자 쓴 책을 먼저. 이름이 같아도 열 명이 함께 쓴 교재보다는
     시인이 혼자 낸 시집을 찾고 있을 때가 많다 — '김소연' 에서 『구강생리학』
     (공저 12명)이 『수학자의 아침』보다 위에 있었다. */
  if (names.length === 1 && names[0] === q) return 2.5;
  if (names.some((a) => a === q)) return 2.2;
  if (names.some((a) => a.startsWith(q))) return 1.5;
  return 0;
}

/* 같은 시리즈가 목록을 다 먹지 않게 두 권까지만. '제네레이션' 으로 찾으면
   『마리오넷 제네레이션』 1~5권이 다섯 자리를 가져갔다. */
const seriesKey = (t) => stripVolume(String(t ?? "")).replace(/\s+/g, "").toLowerCase();

function rank(list, query) {
  const q = String(query ?? "").trim();
  if (!q) return list.slice(0, SHOW);
  /* 저자가 비어 오는 행이 섞여 있다. '잠' 으로 찾으면 저자 없는 『잠』이
     두 번째에 앉아 실제 책을 밀어냈다. 같은 점수 안에서는 저자가 있는
     쪽을 앞에 둔다. */
  const noAuthor = (b) => (!b.author || b.author === "저자 미상" ? 1 : 0);
  const sorted = list
    .map((b, i) => ({ b, i, s: Math.max(titleScore(b.title, q), authorScore(b.author, q), b.altScore ?? 0) }))
    .sort((x, y) => y.s - x.s || noAuthor(x.b) - noAuthor(y.b) || x.i - y.i);
  const seen = new Map();
  const out = [];
  for (const { b } of sorted) {
    const k = seriesKey(b.title);
    const n = seen.get(k) ?? 0;
    if (n >= 2) continue;
    seen.set(k, n + 1);
    out.push(b);
    if (out.length >= SHOW) break;
  }
  return out;
}

export default function useBookSearch(query) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    /* 한 글자도 받는다. 두 글자로 막아 두었더니 『잠』『흰』 같은 한 글자
       제목이 아예 검색되지 않았다. 타이핑 중 매 글자마다 부르는 것은
       아래 700ms 디바운스가 막는다. */
    if (!query || query.trim().length < 1) { setResults([]); setLoading(false); return; }
    clearTimeout(timerRef.current);
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // 1. Kakao (via Cloud Function) → 2. Google Books → 3. Open Library
        const kakaoResults = await searchKakaoMerged(query);
        if (kakaoResults && kakaoResults.length > 0) {
          setResults(rank(kakaoResults, query));
        } else {
          const googleResults = await searchGoogle(query);
          if (googleResults && googleResults.length > 0) {
            setResults(rank(googleResults, query));
          } else {
            const olResults = await searchOpenLibrary(query);
            setResults(rank(olResults || [], query));
          }
        }
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 700);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  return { results, loading };
}
